import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { motion } from 'framer-motion';
import { Flame, TrendingUp, CalendarDays, CheckCircle2, Plus, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [events, setEvents] = useState([]);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState('');
  const [logNotes, setLogNotes] = useState('');
  const [logging, setLogging] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [statsRes, logsRes, activitiesRes, eventsRes] = await Promise.all([
        axios.get(`${API}/karma/stats`, { withCredentials: true }),
        axios.get(`${API}/karma/logs`, { withCredentials: true }),
        axios.get(`${API}/activities`, { withCredentials: true }),
        axios.get(`${API}/events`, { withCredentials: true }),
      ]);
      setStats(statsRes.data); setLogs(logsRes.data); setActivities(activitiesRes.data); setEvents(eventsRes.data);
    } catch (err) { console.error('Dashboard fetch error:', err); }
  };

  const handleLogKarma = async () => {
    if (!selectedActivity) return;
    setLogging(true);
    try {
      await axios.post(`${API}/karma/log`, { activity_id: selectedActivity, notes: logNotes }, { withCredentials: true });
      toast.success('Karma logged! Keep going!');
      setLogDialogOpen(false); setSelectedActivity(''); setLogNotes('');
      fetchData();
    } catch { toast.error('Failed to log karma'); } finally { setLogging(false); }
  };

  const upcomingEvents = events.filter(e => new Date(e.date) >= new Date()).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#110A06]">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12" data-testid="user-dashboard">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="mb-8">
          <motion.p variants={fadeUp} className="font-sans text-xs uppercase tracking-[0.2em] font-bold text-[#D4A54A] mb-1">Dashboard</motion.p>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl tracking-tight text-[#F5E6C8]">
            Namaste, {user?.name?.split(' ')[0] || 'Friend'}
          </motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm text-[#A89070] mt-1">
            {user?.subscription ? `${user.subscription.tier.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} member` : 'No active subscription'}
          </motion.p>
        </motion.div>

        {/* Stats */}
        <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <motion.div variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#D4A54A]/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-[#D4A54A]" strokeWidth={1.5} />
              </div>
              <span className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070]">Karma Points</span>
            </div>
            <p className="font-serif text-4xl font-semibold text-[#D4A54A]" data-testid="karma-points">{stats?.total_points || 0}</p>
          </motion.div>
          <motion.div variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#7D9B6A]/10 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-[#7D9B6A]" strokeWidth={1.5} />
              </div>
              <span className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070]">Activities Done</span>
            </div>
            <p className="font-serif text-4xl font-semibold text-[#F5E6C8]" data-testid="activities-done">{stats?.total_activities || 0}</p>
          </motion.div>
          <motion.div variants={fadeUp} className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#8B6914]/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#C49540]" strokeWidth={1.5} />
              </div>
              <span className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070]">This Month</span>
            </div>
            <p className="font-serif text-4xl font-semibold text-[#F5E6C8]" data-testid="monthly-points">
              {stats?.monthly_breakdown?.[new Date().toISOString().slice(0, 7)] || 0}
            </p>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Log Karma */}
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-xl font-semibold text-[#F5E6C8]">Log Your Karma</h2>
                <Dialog open={logDialogOpen} onOpenChange={setLogDialogOpen}>
                  <DialogTrigger asChild>
                    <button data-testid="log-karma-btn" className="bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-5 py-2 font-sans text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#D4A54A]/20 flex items-center gap-2">
                      <Plus className="w-4 h-4" /> Log Activity
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1A1008] border border-[#3D2D1D] rounded-2xl">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-xl text-[#F5E6C8]">Log a Karma Activity</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Activity</label>
                        <Select value={selectedActivity} onValueChange={setSelectedActivity}>
                          <SelectTrigger data-testid="activity-select" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] h-11">
                            <SelectValue placeholder="Select an activity" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1008] border-[#3D2D1D]">
                            {activities.map(a => (
                              <SelectItem key={a.activity_id} value={a.activity_id} className="text-[#F5E6C8] focus:bg-[#23180E] focus:text-[#D4A54A]">{a.title} (+{a.points} pts)</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="font-sans text-xs uppercase tracking-widest font-bold text-[#A89070] mb-1.5 block">Notes (optional)</label>
                        <Textarea value={logNotes} onChange={(e) => setLogNotes(e.target.value)} data-testid="log-notes" className="rounded-xl border-[#3D2D1D] bg-[#110A06] text-[#F5E6C8] placeholder:text-[#5C4830]" placeholder="How was the experience?" />
                      </div>
                      <button onClick={handleLogKarma} disabled={logging || !selectedActivity} data-testid="confirm-log-btn" className="w-full bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full py-3 font-sans font-bold transition-all duration-200 disabled:opacity-50">
                        {logging ? 'Logging...' : 'Log Karma'}
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {activities.slice(0, 6).map(a => (
                  <button key={a.activity_id} onClick={() => { setSelectedActivity(a.activity_id); setLogDialogOpen(true); }}
                    className="text-left p-4 rounded-xl border border-[#3D2D1D]/50 hover:border-[#D4A54A]/30 hover:bg-[#23180E] transition-all duration-200">
                    <p className="font-sans text-sm font-medium text-[#F5E6C8] truncate">{a.title}</p>
                    <p className="font-sans text-xs text-[#D4A54A] mt-1">+{a.points} pts</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4">Recent Activity</h2>
              {logs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="font-sans text-sm text-[#A89070]">No activities logged yet. Start your karma journey!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {logs.slice(0, 5).map((log, i) => (
                    <div key={log.log_id || i} className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#23180E] transition-colors duration-200">
                      <div className="w-10 h-10 rounded-xl bg-[#7D9B6A]/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-[#7D9B6A]" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium text-[#F5E6C8] truncate">{log.activity_title}</p>
                        {log.notes && <p className="font-sans text-xs text-[#A89070] truncate mt-0.5">{log.notes}</p>}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-sans text-sm font-semibold text-[#D4A54A]">+{log.points}</p>
                        <p className="font-sans text-xs text-[#A89070]">{new Date(log.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4 flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#D4A54A]" strokeWidth={1.5} /> Upcoming Events
              </h2>
              {upcomingEvents.length === 0 ? (
                <p className="font-sans text-sm text-[#A89070]">No upcoming events</p>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.map(evt => (
                    <div key={evt.event_id} className="p-3 rounded-xl border border-[#3D2D1D]/50 hover:border-[#D4A54A]/30 transition-all duration-200">
                      <p className="font-sans text-sm font-medium text-[#F5E6C8]">{evt.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-[#A89070]" />
                        <p className="font-sans text-xs text-[#A89070]">{new Date(evt.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      </div>
                      <p className="font-sans text-xs text-[#D4A54A] mt-1">{evt.participants?.length || 0}/{evt.max_participants} joined</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-[#1A1008] rounded-2xl border border-[#3D2D1D]/50 p-6">
              <h2 className="font-serif text-xl font-semibold text-[#F5E6C8] mb-4">Subscription</h2>
              {user?.subscription ? (
                <div>
                  <div className="inline-block bg-[#D4A54A]/10 text-[#D4A54A] px-3 py-1 rounded-full font-sans text-xs font-bold uppercase tracking-wider mb-2">
                    {user.subscription.tier.replace('_', ' ')}
                  </div>
                  <p className="font-sans text-sm text-[#A89070]">Status: <span className="text-[#7D9B6A] font-medium">Active</span></p>
                </div>
              ) : (
                <div>
                  <p className="font-sans text-sm text-[#A89070] mb-3">No active subscription</p>
                  <a href="/subscription" className="inline-block bg-[#D4A54A] hover:bg-[#C49540] text-[#110A06] rounded-full px-5 py-2 font-sans text-sm font-bold transition-all duration-200">
                    Subscribe Now
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
